
import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Resultados() {
  useEffect(() => {
    // Set page title and meta description
    document.title = 'Resultados e Cases de Sucesso | 7deOuros';
    
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Resultados",
      "description": "Cases de sucesso e resultados reais de empresas que implementaram nosso sistema de vendas",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://setedeouros.com.br'}/resultados`,
      "mainEntity": {
        "@type": "ItemList",
        "name": "Cases de Sucesso",
        "description": "Empresas médias que transformaram seus resultados comerciais",
        "numberOfItems": 6
      }
    });
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-600 to-red-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-8" style={{ fontFamily: 'Sora, sans-serif' }}>
            Resultados que Falam por Si
          </h1>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8">
              <div className="text-4xl font-bold text-orange-400 mb-2">+300</div>
              <p className="text-red-100">Implementações realizadas</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8">
              <div className="text-4xl font-bold text-orange-400 mb-2">+35%</div>
              <p className="text-red-100">Aumento médio em 60 dias</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8">
              <div className="text-4xl font-bold text-orange-400 mb-2">-40%</div>
              <p className="text-red-100">Redução no ciclo de vendas</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Cases Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Cases de Sucesso
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Empresas médias que transformaram seus resultados comerciais
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Card Principal 1 - Paula Raquel */}
            <div className="md:col-span-2 bg-gradient-to-br from-red-50 to-red-100 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-red-200 p-8">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-user-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Paula Raquel</h3>
                  <p className="text-red-600 font-semibold">Diretora de Vendas | Tigre</p>
                </div>
              </div>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                "O que mais me impressionou foi como a equipe ganhou confiança. Eles não fogem mais das objeções, atacam elas."
              </p>
              <div className="flex items-center text-sm text-gray-600">
                <i className="ri-time-line mr-2"></i>
                Resultados em 42 dias
              </div>
            </div>

            {/* Cards Regulares - Linha 1 */}
            <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-computer-line text-xl text-blue-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Tecnologia</h4>
              <p className="text-sm text-gray-600">SaaS &amp; Software</p>
            </div>

            <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-shopping-bag-line text-xl text-purple-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Varejo</h4>
              <p className="text-sm text-gray-600">B2B &amp; Distribuição</p>
            </div>

            {/* Card Principal 2 - Silvio Hilgert */}
            <div className="md:col-span-2 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-orange-200 p-8">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-user-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Silvio Hilgert</h3>
                  <p className="text-orange-600 font-semibold">Head de Educação | SVN</p>
                </div>
              </div>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                "Execução impecável do essencial. É exatamente o que nossos assessores novos precisavam."
              </p>
              <div className="flex items-center text-sm text-gray-600">
                <i className="ri-time-line mr-2"></i>
                Resultados em 35 dias
              </div>
            </div>

            {/* Cards Regulares - Linha 2 */}
            <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-graduation-cap-line text-xl text-green-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Educação</h4>
              <p className="text-sm text-gray-600">Cursos &amp; Treinamentos</p>
            </div>

            <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 text-center">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-heart-pulse-line text-xl text-red-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Saúde</h4>
              <p className="text-sm text-gray-600">Clínicas &amp; Hospitais</p>
            </div>

            <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 text-center">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-service-line text-xl text-teal-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Serviços</h4>
              <p className="text-sm text-gray-600">Consultoria &amp; Jurídico</p>
            </div>

            <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 text-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-truck-line text-xl text-indigo-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Distribuição</h4>
              <p className="text-sm text-gray-600">Atacado &amp; Logística</p>
            </div>

            <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 text-center">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-tools-line text-xl text-yellow-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Manufatura</h4>
              <p className="text-sm text-gray-600">Indústria &amp; Produção</p>
            </div>

            <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 text-center">
              <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-building-line text-xl text-cyan-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Imobiliário</h4>
              <p className="text-sm text-gray-600">Incorporação &amp; Vendas</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-red-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
            Pronto para Estes Resultados?
          </h2>
          <p className="text-xl text-red-100 mb=8">
            Comece com nossa análise gratuita do seu funil de vendas
          </p>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
